import React, { createContext, useContext, useRef, useMemo } from "react";
import { GAME_LAYOUT } from "../settings";

export const ReelRefsContext = createContext(null);

// Initialize HoldAndWin tension overlay refs structure using settings constants
const INITIAL_HOLD_AND_WIN_TENSION_OVERLAYS = Array(GAME_LAYOUT.REEL_COUNT)
  .fill(null)
  .map(() => Array(GAME_LAYOUT.SYMBOLS_VISIBLE).fill(null));

export const ReelRefsProvider = ({ children }) => {
  const reelRefs = useRef([]);
  const overlayReelRefs = useRef([]);
  const infoReelRefs = useRef([]);
  const tensionReelRefs = useRef([]);
  const winLinesContainerRef = useRef(null);
  const winBoxContainerRef = useRef(null);
  const dimReelSetRef = useRef(null);
  const globalLineWinTextRef = useRef(null);
  const lineWinTextRef = useRef(null);

  // Free Spin
  const freeSpinReelRefs = useRef([]);
  const freeSpinOverlayReelRefs = useRef([]);
  const maskReelRefs = useRef([]);
  const freeSpinWinLinesContainerRef = useRef(null);
  const freeSpinSceneTransitionRef = useRef(null);
  const reelSetMaskRef = useRef(null);

  // Free Spin Basic — the Mad Labs bonus board.
  // Its own arrays rather than the Zeus freeSpin* ones above, because both
  // boards can be in the tree at the same time and the two use different
  // components. Every other feature in this game already owns its pair the
  // same way.
  const freeSpinBasicReelRefs = useRef([]);
  const freeSpinBasicOverlayReelRefs = useRef([]);
  const freeSpinBasicReelSetMaskRef = useRef(null);
  // The bonus room's own win display. The base room's copies live inside
  // game_view, which sits at alpha 0.001 for the whole round — sharing them
  // would dim and animate a board nobody can see.
  const freeSpinBasicDimReelSetRef = useRef(null);
  const freeSpinBasicWinBoxContainerRef = useRef(null);
  // The bonus room's own win lines (2026-10-13) — same reason as its boxes.
  const freeSpinBasicWinLinesContainerRef = useRef(null);
  const freeSpinBasicGlobalLineWinTextRef = useRef(null);
  const freeSpinBasicLineWinTextRef = useRef(null);
  // The bonus board's own snap-clear. It MUST NOT share the base game's:
  // HookImport mounts both win controllers, and each assigns
  // snapClearAllRef.current on every render, so the one mounted last silently
  // owned the single ref — the bonus board. ReelControlContext and the
  // transition then cleared the WRONG board.
  const freeSpinBasicSnapClearAllRef = useRef(null);
  // The free-spin play button's sprite. Its own ref, so the two play buttons
  // can never animate each other.
  const freeSpinPlayButtonRef = useRef(null);

  // HoldAndWin
  const holdAndWinReelRefs = useRef([]);
  const holdAndWinOverlayReelRefs = useRef([]);
  const holdAndWinTensionOverlayRefs = useRef(INITIAL_HOLD_AND_WIN_TENSION_OVERLAYS);
  const holdAndWinCollectorControllerRef = useRef(null);
  const holdAndWinCollectorDisplayRef = useRef(null);
  const holdAndWinCoinCollectRef = useRef(null);
  const holdAndWinCounterRef = useRef(null);
  const holdAndWinProgressBarRef = useRef(null);
  const reelFrameDecorationRef = useRef(null);

  // Royal Collect
  const royalCollectReelRefs = useRef([]);
  const royalCollectOverlayReelRefs = useRef([]);
  const royalCollectCollectionLayerRef = useRef(null);
  const royalCollectReelFrontControllerRef = useRef(null);
  const royalCollectRespinCounterRef = useRef(null);

  // Pot Collection
  const potCollectionReelRefs = useRef([]);
  const potCollectionOverlayReelRefs = useRef([]);
  const potCollectionCollectionLayerRef = useRef(null);
  const potCollectionRespinCounterRef = useRef(null);
  const potCollectionCoinCounterRef = useRef(null);
  const pcPlayCounterRef = useRef(null); // play button counter text, controlled by respin counter

  // Button refs for animation triggers
  const playButtonRef = useRef(null);

  // Game container ref for tension shake animation
  const gameContainerRef = useRef(null);

  // Background animation controller ref for background spine animations
  const backgroundAnimationControllerRef = useRef(null);

  // Win celebration controller — published so the dev panel can play a tier
  // sequence without a real round behind it. Nothing in the game path uses it;
  // the real sequence is still started by WinAnimationSecondary's own effect.
  const winAnimationControllerRef = useRef(null);

  // Character animation controller ref
  const characterAnimationControllerRef = useRef(null);

  // Background decoration controller ref for front_mask overlay (tree/flowers)
  const backgroundDecorationControllerRef = useRef(null);

  // Jackpots controller refs — separate for base game and RC to avoid ref collision
  const jackpotsControllerRef = useRef(null);     // base game
  const rcJackpotsControllerRef = useRef(null);   // Royal Collect bonus

  // POT Charging — animator ref for Zeus symbol → POT particle trails
  const potChargingAnimatorRef = useRef(null);

  // POT Controllers — { potType: PotController } for trail arrival feedback
  const potControllersRef = useRef(null);

  // Free-spin level charging — the trail from a landed scatter to the level
  // box it fills. The bonus scene owns it, the free-spin win controller fires
  // it, exactly as potChargingAnimatorRef pairs GameView with the landing
  // animations.
  const levelChargingAnimatorRef = useRef(null);

  // Golden Mystery trails — the multiplier reaching across the board to the
  // coins it multiplies, and the collector reaching out to the ones it takes.
  // GameView owns the layer and builds the animator; the base win controller
  // fires it. Same pairing as potChargingAnimatorRef and levelChargingAnimatorRef.
  const goldenTrailAnimatorRef = useRef(null);

  // The "+N free spins" popup. The level-up sequence drives it directly —
  // it has to hand the popup its level and count, and be told when the popup
  // reaches idle and when it closes.
  const additionalFreeSpinsControllerRef = useRef(null);

  // GameView controller ref — for triggering scene transitions (GVC.playTransition)
  const gameViewControllerRef = useRef(null);

  // BonusGameView controller ref — the bonus scene is its own spine in this
  // game, so the swap between the two needs to reach both controllers
  const bonusGameViewControllerRef = useRef(null);

  // FreeSpinsGameView controller ref — the Cleopatra's Fire free spins room.
  // Replaces BonusGameView, which is kept on disk but no longer mounted.
  const freeSpinsGameViewControllerRef = useRef(null);

  // HoldAndWinGameView controller ref — the Cleopatra's Fire Hold & Win room.
  const holdAndWinGameViewControllerRef = useRef(null);
  // The 5 x 8 board's respin handle (spinEmpty / land / quickStop / freeze),
  // published by HoldAndWinBoard, driven by HoldAndWinControlContext.
  const holdAndWinBoardRef = useRef(null);
  // The free spins room's coin → logo trail layer: { trailContainer,
  // symbolHeight }, published by FreeSpinsGameView (see coinToLogoTrail).
  const freeSpinsCoinTrailRef = useRef(null);
  // The respin counter (HoldAndWinCounterController), in the room's
  // respin_counter_placeholder slot.
  const holdAndWinRespinCounterRef = useRef(null);

  // Synchronous visual cleanup callback — populated by useWinController,
  // called by ReelControlContext.handleSpin to clear all win artifacts in ONE frame.
  const snapClearAllRef = useRef(null);



  const refs = useMemo(
    () => ({
      reelRefs,
      overlayReelRefs,
      infoReelRefs,
      tensionReelRefs,
      winLinesContainerRef,
      winBoxContainerRef,
      dimReelSetRef,
      globalLineWinTextRef,
      lineWinTextRef,
      // Free Spin
      freeSpinReelRefs,
      freeSpinOverlayReelRefs,
      maskReelRefs,
      freeSpinWinLinesContainerRef,
      freeSpinSceneTransitionRef,
      reelSetMaskRef,
      // Free Spin Basic
      freeSpinBasicReelRefs,
      freeSpinBasicOverlayReelRefs,
      freeSpinBasicReelSetMaskRef,
      freeSpinBasicDimReelSetRef,
      freeSpinBasicWinBoxContainerRef,
      freeSpinBasicWinLinesContainerRef,
      freeSpinBasicGlobalLineWinTextRef,
      freeSpinBasicLineWinTextRef,
      freeSpinBasicSnapClearAllRef,
      freeSpinPlayButtonRef,
      // HoldAndWin
      holdAndWinReelRefs,
      holdAndWinOverlayReelRefs,
      holdAndWinTensionOverlayRefs,
      holdAndWinCollectorControllerRef,
      holdAndWinCollectorDisplayRef,
      holdAndWinCoinCollectRef,
      holdAndWinCounterRef,
      holdAndWinProgressBarRef,
      reelFrameDecorationRef,
      // Royal Collect
      royalCollectReelRefs,
      royalCollectOverlayReelRefs,
      royalCollectCollectionLayerRef,
      royalCollectReelFrontControllerRef,
      royalCollectRespinCounterRef,
      // Pot Collection
      potCollectionReelRefs,
      potCollectionOverlayReelRefs,
      potCollectionCollectionLayerRef,
      potCollectionRespinCounterRef,
      potCollectionCoinCounterRef,
      pcPlayCounterRef,
      // Button refs for animation triggers
      playButtonRef,
      // Game container ref for tension shake animation
      gameContainerRef,
      // Background animation controller ref
      backgroundAnimationControllerRef,
      winAnimationControllerRef,
      // Character animation controller ref
      characterAnimationControllerRef,
      // Background decoration controller ref
      backgroundDecorationControllerRef,
      // Jackpots controller refs
      jackpotsControllerRef,
      rcJackpotsControllerRef,
      // POT Charging
      potChargingAnimatorRef,
      potControllersRef,
      levelChargingAnimatorRef,
      goldenTrailAnimatorRef,
      additionalFreeSpinsControllerRef,
      // GameView controller
      gameViewControllerRef,
      // BonusGameView controller
      bonusGameViewControllerRef,
      // FreeSpinsGameView controller
      freeSpinsGameViewControllerRef,
      // HoldAndWinGameView controller
      holdAndWinGameViewControllerRef,
      holdAndWinBoardRef,
      holdAndWinRespinCounterRef,
      freeSpinsCoinTrailRef,
      // Synchronous visual cleanup
      snapClearAllRef,
    }),
    []
  );

  return (
    <ReelRefsContext.Provider value={refs}>{children}</ReelRefsContext.Provider>
  );
};

export const useReelRefs = () => {
  const ctx = useContext(ReelRefsContext);
  if (!ctx) throw new Error("useReelRefs must be used within ReelRefsProvider");
  return ctx;
};
